import React, { useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  BookOpen,
  Bookmark,
  History,
  Settings,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { PageSlider } from './PageSlider';

export interface ReaderControlsProps {
  currentPage: number;
  totalPages: number;
  chapterTitle: string;
  isMuted: boolean;
  volume: number;
  isCurrentPageBookmarked: boolean;
  onPrev: () => void;
  onNext: () => void;
  onStep: (step: number) => void;
  onGotoPage: (page: number) => void;
  onToggleToc: () => void;
  onToggleBookmarks: () => void;
  onToggleBacklog: () => void;
  onToggleSettings: () => void;
  onToggleMute: () => void;
  onToggleBookmarkCurrent: () => void;
}

export const ReaderControls: React.FC<ReaderControlsProps> = ({
  currentPage,
  totalPages,
  chapterTitle,
  isMuted,
  isCurrentPageBookmarked,
  onPrev,
  onNext,
  onStep,
  onGotoPage,
  onToggleToc,
  onToggleBookmarks,
  onToggleBacklog,
  onToggleSettings,
  onToggleMute,
  onToggleBookmarkCurrent,
}) => {
  const [jumpInput, setJumpInput] = useState('');
  const [jumpError, setJumpError] = useState(false);

  const handleJumpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseInt(jumpInput.trim(), 10);
    if (isNaN(parsed)) {
      setJumpError(true);
      return;
    }
    setJumpError(false);
    onGotoPage(parsed);
    setJumpInput('');
  };

  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages;
  const progressPercent = (((currentPage - 1) / (totalPages - 1)) * 100).toFixed(1);

  return (
    <nav className="caesura-controls-bar" aria-label="Reader Navigation Controls">
      {/* Top Slider Row */}
      <div className="controls-slider-row">
        <PageSlider
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onGotoPage}
          chapterTitle={chapterTitle}
        />
      </div>

      {/* Main Action Strip */}
      <div className="controls-action-strip">
        {/* Left Drawer Triggers */}
        <div className="controls-group-left">
          <button
            className="control-icon-btn"
            onClick={onToggleToc}
            title="Table of Contents (T)"
            aria-label="Table of Contents"
          >
            <BookOpen size={18} />
            <span className="control-btn-label">TOC</span>
          </button>

          <button
            className={`control-icon-btn ${isCurrentPageBookmarked ? 'active-highlight' : ''}`}
            onClick={onToggleBookmarkCurrent}
            title={isCurrentPageBookmarked ? 'Remove Bookmark' : 'Bookmark Page'}
            aria-label="Bookmark Current Page"
          >
            <Bookmark size={18} fill={isCurrentPageBookmarked ? 'currentColor' : 'none'} />
            <span className="control-btn-label">Mark</span>
          </button>

          <button
            className="control-icon-btn"
            onClick={onToggleBookmarks}
            title="Bookmarks Drawer (B)"
            aria-label="Bookmarks Drawer"
          >
            <Bookmark size={18} />
            <span className="control-btn-label">List</span>
          </button>

          <button
            className="control-icon-btn"
            onClick={onToggleBacklog}
            title="Reading Backlog (L)"
            aria-label="Reading Backlog"
          >
            <History size={18} />
            <span className="control-btn-label">Log</span>
          </button>
        </div>

        {/* Center Page Nav Buttons & Counter */}
        <div className="controls-group-center">
          <button
            className="control-nav-btn"
            onClick={() => onStep(-10)}
            disabled={isFirst}
            title="Step back 10 pages"
            aria-label="Step back 10 pages"
          >
            <ChevronsLeft size={18} />
          </button>

          <button
            className="control-nav-btn control-nav-primary"
            onClick={onPrev}
            disabled={isFirst}
            title="Previous Page (Left Arrow)"
            aria-label="Previous Page"
          >
            <ChevronLeft size={20} />
            <span className="nav-btn-text">Prev</span>
          </button>

          {/* Current Page Pill with Direct Jump Form */}
          <div className="page-counter-pill">
            <span className="counter-current">{currentPage}</span>
            <span className="counter-divider">/</span>
            <span className="counter-total">{totalPages}</span>
            <span className="counter-percent">({progressPercent}%)</span>
          </div>

          <button
            className="control-nav-btn control-nav-primary"
            onClick={onNext}
            disabled={isLast}
            title="Next Page (Right Arrow or Space)"
            aria-label="Next Page"
          >
            <span className="nav-btn-text">Next</span>
            <ChevronRight size={20} />
          </button>

          <button
            className="control-nav-btn"
            onClick={() => onStep(10)}
            disabled={isLast}
            title="Step forward 10 pages"
            aria-label="Step forward 10 pages"
          >
            <ChevronsRight size={18} />
          </button>
        </div>

        {/* Right Aux Controls: Direct Jump & Settings */}
        <div className="controls-group-right">
          <form onSubmit={handleJumpSubmit} className="direct-jump-form">
            <input
              type="number"
              min={1}
              max={totalPages}
              placeholder="Page #"
              value={jumpInput}
              onChange={(e) => {
                setJumpInput(e.target.value);
                setJumpError(false);
              }}
              className={`jump-input ${jumpError ? 'jump-input-error' : ''}`}
              aria-label="Jump directly to page number"
            />
            <button type="submit" className="jump-submit-btn">
              Go
            </button>
          </form>

          <button
            className={`control-icon-btn ${isMuted ? 'muted-active' : ''}`}
            onClick={onToggleMute}
            title={isMuted ? 'Unmute Audio' : 'Mute Audio'}
            aria-label={isMuted ? 'Unmute Audio' : 'Mute Audio'}
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>

          <button
            className="control-icon-btn"
            onClick={onToggleSettings}
            title="Reader Settings (S)"
            aria-label="Settings"
          >
            <Settings size={18} />
          </button>
        </div>
      </div>
    </nav>
  );
};
